import { useState } from 'react';
import { Modal, Text } from '@figma/fpl-components';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { FF_ANNUAL_DISCOUNT_30 } from '@/consts/featureFlags';
import { getAnnualDiscountPercent, getPricingDetails } from '@/consts/pricing.consts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { I18N_KEYS } from '@/language/keys';
import { BillingCycle } from '@/types/shared';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { BillingCycleToggle } from './BillingCycleToggle';
import { modalStyles } from './WeavyPaywallModal.stylex';
import { WeavyPaywallCurrentPlanCard, WeavyPaywallUpsellPlanCard } from './WeavyPaywallPlanCard';
import type { WeavyPaywallEntrypointConfig, WeavyPaywallModalAction } from '@/consts/weavyPaywallEntrypoints.consts';

/** Named arguments emitted when a user acts on the paywall modal. */
export interface WeavyPaywallModalActionParams {
  action: WeavyPaywallModalAction;
  billingCycle: BillingCycle;
}

interface WeavyPaywallModalProps {
  entrypointConfig: WeavyPaywallEntrypointConfig;
  defaultBillingCycle?: BillingCycle;
  isCheckoutLoading: boolean;
  onAction: (params: WeavyPaywallModalActionParams) => void;
  onBillingCycleChange: (billingCycle: BillingCycle) => void;
}

/** Renders the two-tier consumption paywall from the Weavy paywall entrypoint configuration. */
export function WeavyPaywallModal({
  entrypointConfig,
  defaultBillingCycle = BillingCycle.Yearly,
  isCheckoutLoading,
  onAction,
  onBillingCycleChange,
}: WeavyPaywallModalProps) {
  const { t } = useTranslation();
  const isLightMode = useThemeMode() === 'light';
  // A late flag response can update the default, but must not overwrite an explicit choice.
  const [selectedBillingCycle, setBillingCycle] = useState<BillingCycle>();
  const billingCycle = selectedBillingCycle ?? defaultBillingCycle;
  const { value: annualDiscount30Flag } = useFeatureFlag(FF_ANNUAL_DISCOUNT_30);
  const isAnnualDiscount30Enabled = annualDiscount30Flag === true;
  const annualDiscountPercent = getAnnualDiscountPercent(isAnnualDiscount30Enabled);
  const annualDiscountLabel = t(
    isAnnualDiscount30Enabled
      ? I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY_SAVE_DISCOUNT
      : I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY_FIXED_DISCOUNT,
    { discountPercent: annualDiscountPercent },
  );
  const secondaryAction = entrypointConfig.secondaryAction;
  const pricingDetails = getPricingDetails(t, { isAnnualDiscount30Enabled });
  const currentPlan = pricingDetails[entrypointConfig.planTier];
  const targetPricingDetails = pricingDetails[entrypointConfig.targetPlanTier];
  const { highlightedFeature } = entrypointConfig;
  const targetPlan = highlightedFeature
    ? {
        ...targetPricingDetails,
        features: [{ text: t(highlightedFeature), isUnavailable: false }, ...targetPricingDetails.features],
      }
    : targetPricingDetails;

  return (
    <Modal.Root
      open
      onClose={() => onAction({ action: { type: 'close' }, billingCycle })}
      width="fit-content"
      height="fixed"
    >
      <Modal.Contents {...stylex.props(modalStyles.modal, isLightMode && modalStyles.lightModal)}>
        <Modal.HiddenTitle>{t(entrypointConfig.copy.title)}</Modal.HiddenTitle>
        <Modal.Body {...stylex.props(modalStyles.bodyPadding)}>
          <div {...stylex.props(modalStyles.body)}>
            <header {...stylex.props(modalStyles.header)}>
              <div {...stylex.props(modalStyles.headerCopy)}>
                <Text.h2 variant="heading-large" {...stylex.props(modalStyles.headerText)}>
                  {t(entrypointConfig.copy.title)}
                </Text.h2>
                <Text.p variant="body-large" color="secondary" {...stylex.props(modalStyles.headerText)}>
                  {t(entrypointConfig.copy.description, entrypointConfig.copy.descriptionValues)}
                </Text.p>
              </div>
              <BillingCycleToggle
                annualDiscountLabel={annualDiscountLabel}
                isAnnualDiscount30Enabled={isAnnualDiscount30Enabled}
                onChange={(newBillingCycle) => {
                  setBillingCycle(newBillingCycle);
                  onBillingCycleChange(newBillingCycle);
                }}
                value={billingCycle}
              />
            </header>

            <div {...stylex.props(modalStyles.cards)}>
              <WeavyPaywallCurrentPlanCard
                billingCycle={billingCycle}
                ctaLabel={I18N_KEYS.PAYMENTS.PRICING_TABLE.CURRENT_PLAN_CTA}
                plan={currentPlan}
              />
              <WeavyPaywallUpsellPlanCard
                billingCycle={billingCycle}
                ctaLabel={entrypointConfig.primaryAction.label}
                isLoading={isCheckoutLoading}
                onAction={() => onAction({ action: entrypointConfig.primaryAction, billingCycle })}
                plan={targetPlan}
              />
            </div>
          </div>

          {/* Footer with see all plans link */}
          <Text.p variant="body-large" color="secondary" {...stylex.props(modalStyles.footerText)}>
            {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.FREE_PLAN_PAYWALL.LOOKING_FOR_MORE)}{' '}
            <button
              type="button"
              {...stylex.props(modalStyles.footerAction)}
              onClick={() => onAction({ action: secondaryAction, billingCycle })}
            >
              {t(secondaryAction.label)}
            </button>
          </Text.p>
        </Modal.Body>
      </Modal.Contents>
    </Modal.Root>
  );
}
